import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Text, Badge, EmptyState, Modal } from '@/components/design-system'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import TokenPlanFormFields from './TokenPlanFormFields'

interface TokenPlanRow {
  id: string
  name: string
  description: string | null
  slug: string
  plan_rank: number
  daily_token_limit: number | null
  daily_budget_usd: string | null
  active_assignments_count: number
}

interface Pagination { page: number; totalPages: number; total: number }
interface Props { token_plans: TokenPlanRow[]; pagination: Pagination; search: string }

function formatLimit(limit: number | null): string {
  if (limit === null) return 'Ilimitado'
  return limit.toLocaleString('pt-BR')
}

function formatBudget(value: string | null): string {
  if (!value) return 'Sem cap'
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })
}

export default function Index({ token_plans, pagination, search }: Props) {
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<TokenPlanRow | null>(null)
  const [query, setQuery] = useState(search)

  const createForm = useForm({ name: '', description: '', plan_rank: '', daily_token_limit: '', daily_budget_usd: '' })
  const editForm = useForm({ name: '', description: '', plan_rank: '', daily_token_limit: '', daily_budget_usd: '' })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/token_plans', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) => router.get('/cc/token_plans', { search, page }, { preserveState: true })

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    createForm.reset()
  }

  const handleCreate = () => createForm.post('/cc/token_plans', { onSuccess: closeCreateModal })

  const handleEdit = (plan: TokenPlanRow) => {
    setSelectedPlan(plan)
    editForm.setData({
      name: plan.name,
      description: plan.description ?? '',
      plan_rank: String(plan.plan_rank),
      daily_token_limit: plan.daily_token_limit !== null ? String(plan.daily_token_limit) : '',
      daily_budget_usd: plan.daily_budget_usd ?? '',
    })
    setEditModalOpen(true)
  }

  const handleUpdate = () => {
    if (!selectedPlan) return
    editForm.patch(`/cc/token_plans/${selectedPlan.id}`, {
      onSuccess: () => { setEditModalOpen(false); setSelectedPlan(null) },
    })
  }

  const handleDeleteClick = (plan: TokenPlanRow) => { setSelectedPlan(plan); setDeleteModalOpen(true) }
  const handleDelete = () => {
    if (!selectedPlan) return
    router.delete(`/cc/token_plans/${selectedPlan.id}`, {
      onSuccess: () => { setDeleteModalOpen(false); setSelectedPlan(null) },
    })
  }

  const hasNoPlansAtAll = token_plans.length === 0 && !search
  const hasNoSearchResults = token_plans.length === 0 && !!search
  const hasActiveAssignments = (selectedPlan?.active_assignments_count ?? 0) > 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">Planos de Token</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome ou slug..."
                aria-label="Buscar planos"
                className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72"
              />
            </div>
            <Button type="submit" variant="outline" size="sm">Buscar</Button>
          </form>
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />Novo Plano
          </Button>
        </div>
      </div>

      {hasNoPlansAtAll ? (
        <EmptyState
          title="Nenhum plano cadastrado"
          description="Crie um plano de token para gerenciar limites de uso dos agentes."
          action={{ label: 'Novo Plano', onClick: () => setCreateModalOpen(true) }}
        />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ranking</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Limite Diário</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Budget USD</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Agentes Ativos</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {token_plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-accent transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{plan.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{plan.slug}</td>
                    <td className="px-4 py-3"><Badge>{plan.plan_rank}</Badge></td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatLimit(plan.daily_token_limit)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatBudget(plan.daily_budget_usd)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={plan.active_assignments_count > 0 ? 'info' : undefined}>
                        {plan.active_assignments_count}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(plan)} aria-label={`Editar ${plan.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(plan)} aria-label={`Excluir ${plan.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {hasNoSearchResults && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum plano encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
              <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal open={createModalOpen} onClose={closeCreateModal} title="Novo Plano"
        primaryAction={{ content: 'Criar', onAction: handleCreate, loading: createForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: closeCreateModal }}>
        <TokenPlanFormFields data={createForm.data} errors={createForm.errors} setData={createForm.setData} />
      </Modal>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title="Editar Plano"
        primaryAction={{ content: 'Salvar', onAction: handleUpdate, loading: editForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: () => setEditModalOpen(false) }}>
        <TokenPlanFormFields data={editForm.data} errors={editForm.errors} setData={editForm.setData} />
      </Modal>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Excluir Plano"
        destructiveAction={{ content: 'Excluir', onAction: handleDelete, disabled: hasActiveAssignments }}
        secondaryAction={{ content: 'Cancelar', onAction: () => setDeleteModalOpen(false) }}>
        <Text>Tem certeza que deseja excluir o plano <strong>{selectedPlan?.name}</strong>?</Text>
        {hasActiveAssignments && (
          <p className="mt-2 text-sm text-red-600">
            Este plano possui {selectedPlan?.active_assignments_count} agente(s) atribuído(s). Não será possível excluí-lo enquanto houver agentes vinculados.
          </p>
        )}
      </Modal>
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>
